import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {Proposal, Tender, TenderStatus} from '../../../../../../../domain/funding-tender/funding-tender-data';
import {WidgetDetail} from '../../../../../../../domain/widget/model/widget-detail';
import {Duration} from '../../../../../../../domain/shared/duration';
import {Chart} from 'angular-highcharts';
import {WidgetSize} from '../../../../../../../domain/widget/model/widget-size.enum';
import {TranslateService} from '@ngx-translate/core';
import {DomainUrlService} from '../../../../../../../application/config/domain-url.service';
import {UserContextService} from '../../../../../../../application/app-context/user-context/user-context.service';
import * as Highcharts from 'highcharts';

@Component({
  selector: 'app-widget-funding-tender-ongoing-analysis',
  templateUrl: './home-dashboard-widget-funding-tender-ongoing-analysis.component.html'
})
export class HomeDashboardWidgetFundingTenderOngoingAnalysisComponent implements OnInit {

  @Input() widget: WidgetDetail;
  @Input() tender: Tender;
  proposalsForChart: Proposal[];
  durationsList: Duration[];
  durationChoosen: Duration;
  WidgetSize = WidgetSize;
  chart: Chart;
  urlLogoMap = new Map<number, string>();

  @Output() actionEvent = new EventEmitter<string>();

  constructor(private translateService: TranslateService,
              private domainUrlService: DomainUrlService,
              private userContextService: UserContextService) {
  }

  ngOnInit(): void {
    if (this.tender) {
      this.initDurationsList();
      this.durationChoosen = this.durationsList[0];
      this.loadProposalsForChart();
    }
  }

  public goToFundingTenderReceiptPage(): string {
    let receiptPageUrl = this.domainUrlService.getFundingtenderUrl() + '/tender/' + this.tender.id + '/receipt';
    const accountId = this.userContextService.getUserContext().accountId;
    receiptPageUrl += accountId != null ? '?accountId=' + accountId : '';
    return receiptPageUrl;
  }

  public isFirstDurationSelected(): boolean {
    return this.durationsList.indexOf(this.durationChoosen) === 0;
  }

  public isLastDurationSelected(): boolean {
    return this.durationsList.indexOf(this.durationChoosen) === this.durationsList.length - 1;
  }

  public nextDuration(): void {
    this.durationChoosen = this.durationsList[this.durationsList.indexOf(this.durationChoosen) + 1];
    this.loadProposalsForChart();
    this.actionEvent.emit('widget.amplitude.carousel-use');
  }

  public previousDuration(): void {
    this.durationChoosen = this.durationsList[this.durationsList.indexOf(this.durationChoosen) - 1];
    this.loadProposalsForChart();
    this.actionEvent.emit('widget.amplitude.carousel-use');
  }

  private initDurationsList(): void {
    this.durationsList = [];
    for (const proposal of this.tender.proposals) {
      const duration = new Duration(proposal.consolidationPeriodInYear, proposal.consolidationPeriodInMonth);
      if (!this.durationsList.some(d => d.year === duration.year && d.month === duration.month)) {
        this.durationsList.push(duration);
      }
    }
    this.sortDurations();
  }

  private sortDurations(): void {
    this.durationsList.sort((d1, d2) => {
      return d1.year === d2.year ? d1.month - d2.month : d1.year - d2.year;
    });
  }

  private loadProposalsForChart(): void {
    this.proposalsForChart = this.tender.proposals
      .filter(p => {
        return p.consolidationPeriodInYear === this.durationChoosen.year
          && p.consolidationPeriodInMonth === this.durationChoosen.month;
      })
      .sort((p1, p2) => {
        return p1.euriborMarginUsedForAnalyse - p2.euriborMarginUsedForAnalyse;
      })
      .slice(0, this.widget.size === WidgetSize.SMALL ? 3 : 7);
    if (this.proposalsForChart && this.proposalsForChart.length > 0) {
      this.loadChart();
    }
  }

  private loadChart(): void {
    const urlLogoMap = this.urlLogoMap;
    const defaultLogoUrl = this.domainUrlService.getFundingtenderUrl().concat('/assets/bank/MISCELANOUS.png');

    const spec = {
      credits: {
        enabled: false
      },
      chart: {
        type: 'column',
        spacing: [1, 1, 1, 1],
        marginTop: 8,
        spacingTop: 8,
        spacingBottom: 26,
        marginBottom: 26,
        plotBorderColor: '#6a7480',
        backgroundColor: '#ffffff',
        plotBorderWidth: 0,
        style: {
          fontFamily: 'Arial, Helvetica, sans-serif',
          'font-family': 'Arial, Helvetica, sans-serif'
        }
      },
      colors: ['#1c3966', '#e95053', '#f59700', '#008CBA', '#9F1BB7'],
      title: {
        text: null
      },
      subtitle: {
        text: null
      },
      exporting: {
        enabled: false
      },
      accessibility: {
        announceNewData: {
          enabled: true
        }
      },
      xAxis: {
        className: 'widget-highcharts-yaxis',
        title: {
          text: null
        },
        type: 'category',
        gridLineColor: '#CCCCCC',
        gridLineDashStyle: 'Dash',
        gridLineWidth: 0,
        lineColor: '#CCCCCC',
        tickPosition: 'inside',
        tickmarkPlacement: 'on',
        tickWidth: 0,
        tickColor: '#CCCCCC',
        labels: {
          enabled: true,
          padding: 1,
          y: 14,
          style: {
            color: '#000000',
            fontSize: '9px',
            fontWeight: 'normal'
          },
          useHTML: true,
          formatter(): string {
            return '<span class="i-block" style="border-radius:50%;width: 19px; height: 19px;background-size: cover;'
              + 'background-image: url(' + urlLogoMap.get(this.value) + '), url(' + defaultLogoUrl + ')"></span>';
          }
        },
        style: {
          fontFamily: 'Arial, Helvetica, sans-serif'
        }
      },
      yAxis: {
        className: 'widget-highcharts-yaxis',
        title: {
          text: null
        },
        gridLineColor: '#CCCCCC',
        gridLineDashStyle: 'Dash',
        gridLineWidth: 1,
        lineColor: '#CCCCCC',
        lineWidth: 1,
        labels: {
          enabled: false,
          x: -8,
          format: '{value:,.2f}%',
          style: {
            color: '#37404E',
            fontSize: '9px',
            fontWeight: 'normal'
          }
        },
        tickPosition: 'inside',
        tickWidth: 0,
        tickLength: 0,
        tickColor: '#6a7480',
        tickInterval: 0.5,
        style: {
          fontFamily: 'Arial, Helvetica, sans-serif',
          'font-family': 'Arial, Helvetica, sans-serif'
        }
      },
      legend: {
        enabled: false,
        itemStyle: {
          lineHeight: '12px',
          fontSize: '10px',
          color: '#6A7480'
        },
        style: {
          fontFamily: 'Arial, Helvetica, sans-serif',
          'font-family': 'Arial, Helvetica, sans-serif'
        }
      },
      plotOptions: {
        column: {
          borderWidth: 0,
          borderRadiusTopLeft: 5,
          borderRadiusTopRight: 5,
          dataLabels: {
            padding: 3,
            crop: false,
            overflow: 'none',
            inside: false,
            y: 0,
            style: {
              fontWeight: 'normal',
              fontSize: '10px'
            },
            enabled: true,
            format: '{point.y:.2f}%'
          }
        }
      },
      tooltip: {
        headerFormat: '<span style="font-size:11px">{series.name}</span><br>',
        pointFormat: '<span style="color:{point.color}">{point.bank}</span>: <b>{point.y:.2f}%</b><br/>',
        backgroundColor: '#FFFFFF',
        outside: true
      },
      series: [
        {
          type: 'column',
          name: this.translateService.instant('widget.funding-tender.best-offers-for') +
            this.translateService.instant('widget.funding-tender.duration',
              {years: this.durationChoosen.year, months: this.durationChoosen.month}),
          maxPointWidth: 8,
          pointWidth: 10,
          data: this.getData()
        }
      ]
    };

    this.chart = new Chart(spec as Highcharts.Options);
  }

  private getData(): any {
    const data = [];
    for (let i = 0; i < this.proposalsForChart.length; i++) {
      const proposal = this.proposalsForChart[i];
      data.push({
        bank: proposal.lender.label,
        color: this.getColorFromRateType(proposal),
        y: proposal.euriborMarginUsedForAnalyse
      });
      this.urlLogoMap.set(i, proposal.lender.urlLogo);
    }
    return data;
  }

  private getColorFromRateType(proposal: Proposal): string {
    if (proposal.rateType === 'FIXED_RATE') {
      return '#93CA4A';
    } else if (proposal.rateType === 'FLOATING_RATE') {
      return '#1592A7';
    } else {
      return '#711382';
    }
  }

}
